import { FallbackProps } from 'react-error-boundary';
import { useEffect, useMemo } from 'react';

type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(err: unknown): err is ErrorWithMessage {
  return (
    err !== undefined &&
    err !== null &&
    typeof err === 'object' &&
    'message' in err &&
    typeof err.message === 'string'
  );
}

/**
 * @description Fallback Component for ReactErrorBoundary. Probably will never display but just in case. React Router Dom catches most of the unhandle Errors
 * @author Jacob Cutshall
 */
const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const isChunkLoadError = useMemo(() => {
    if (isErrorWithMessage(error)) {
      return error.message.match(/Loading chunk [\d]+ failed/) !== null;
    }
    return false;
  }, [error]);

  useEffect(() => {
    let myTo: NodeJS.Timeout | undefined;
    if (isChunkLoadError) {
      myTo = setTimeout(() => {
        window.location.reload();
      }, 10000);
    }

    return () => {
      if (myTo) {
        clearTimeout(myTo);
      }
    };
  }, [isChunkLoadError]);

  if (isChunkLoadError) {
    return (
      <div role="alert">
        <p>
          The Application has been updated. Please Refresh the page or wait 10
          seconds.
        </p>
        <button onClick={() => window.location.reload()}>Refresh</button>
      </div>
    );
  }

  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{isErrorWithMessage(error) ? error.message : String(error)}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  );
};

export default ErrorFallback;
